// Stored JSON details (audit entries, alerts) are written with camelCase keys since the 2026-10 backend refactor;
// older rows keep their snake_case keys. Readers normalise once and then use camelCase only.

const camel = (k: string) => k.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());

const isPlain = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Top-level keys in camelCase (a camelCase key wins over its snake_case twin). */
function camelKeys(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    const c = camel(k);
    if (c === k || !(c in o)) out[c] = v;
  }
  return out;
}

const cache = new WeakMap<object, Record<string, unknown>>();

/**
 * A stored detail with camelCase keys: its own keys and those of the objects it nests (`before`, `after`,
 * `snapshot`, `filters`); values (gateway payloads, command args) stay as stored. Cached per object.
 */
export function camelDetail(detail: Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!detail) return {};
  const hit = cache.get(detail);
  if (hit) return hit;
  const top = camelKeys(detail);
  for (const k of ['before', 'after', 'snapshot', 'filters']) if (isPlain(top[k])) top[k] = camelKeys(top[k] as Record<string, unknown>);
  cache.set(detail, top);
  return top;
}
